import translate from '@translate';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FormBlock from 'src/components/FormBlock';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { WebhookRecordType } from 'src/types/webhook';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import Detail from './Detail';
import TopBlock from './TopBlock';
import HistoryBlock from './HistoryBlock';
import {
    clearWebhookDetail,
    createWebhookDetailFetch,
    deleteWebhookFetch,
    getWebhookDetailFetch,
    getWebhookDetailHistoryFetch,
    updateWebhookDetailFetch,
} from 'src/resources/webhookDetail/reducer';
import { addMessage } from 'src/resources/alertMessages/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EDIT_KEYS, EditKeysType } from './constants';
import { WebhookMethod, WebhookPayloadVersion, WebhookType } from 'skladon-types';
import { ConstantsCodesObjectType } from 'src/types/constantsCodes';

const initData: Partial<WebhookRecordType> = {
    ACTIVE: 1,
    METHOD: WebhookMethod.POST,
    URL: '',
    WEBHOOK_TYPE: WebhookType.CreateOrder,
    PARAMETERS: null,
    PAYLOAD_VERSION: WebhookPayloadVersion.V21,
};

const WebhookDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { idWebhook } = useParams();

    const newWebhook = !idWebhook;
    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.webhookDetail.data);
    const changelog = useSelector((state: RootStoreType) => state.webhookDetail.changelog);
    const loading = useSelector((state: RootStoreType) => state.webhookDetail.loading);
    const deleted = useSelector((state: RootStoreType) => state.webhookDetail.deleted);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const boolNumberCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.boolNumberCodes);

    const [editData, setEditData] = useState<WebhookRecordType>(
        getEditData(initData || {}, EDIT_KEYS) as WebhookRecordType
    );
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [activeTab, setActiveTab] = useState<string>('detail');

    const permissionCreate = useCheckPermission('WEBHOOK.CREATE');
    const permissionUpdate = useCheckPermission('WEBHOOK.UPDATE');
    const disableEdit = newWebhook ? !permissionCreate : !permissionUpdate;
    const permissionDelete = useCheckPermission('WEBHOOK.DELETE');
    const disableDelete = newWebhook || !permissionDelete;

    const backUrl = '/settings/webhooks/';

    const webhookTypeCodesObject = useMemo(
        () =>
            Object.values(WebhookType).reduce<ConstantsCodesObjectType>((acc, value) => {
                acc[value] = { value, label: translate(`webhooks.webhookType.${value}`) };
                return acc;
            }, {}),
        []
    );

    const methodCodesObject = useMemo(
        () =>
            Object.values(WebhookMethod).reduce<ConstantsCodesObjectType>((acc, value) => {
                acc[value] = { value, label: value };
                return acc;
            }, {}),
        []
    );

    useEffect(() => {
        if (idWebhook) {
            dispatch(setLocationParams({ name: translate('webhookDetail.title', { idWebhook }), backUrl }));
        } else {
            dispatch(setLocationParams({ name: translate('webhookDetail.titleNew'), backUrl }));
        }
    }, [idWebhook, dispatch, backUrl]);

    useEffect(() => {
        if (idWebhook) {
            dispatch(getWebhookDetailFetch(idWebhook));
        } else {
            setEditData(getEditData(initData || {}, EDIT_KEYS) as WebhookRecordType);
        }
        return () => {
            dispatch(clearWebhookDetail());
        };
    }, [idWebhook, dispatch]);

    useEffect(() => {
        if (data) {
            setEditData(data);
        }
        if (!idWebhook && data?.ID) {
            navigate(`/settings/webhooks/${String(data.ID)}/`);
        }
    }, [data, idWebhook, navigate]);

    useEffect(() => {
        if (deleted) {
            navigate(backUrl);
        }
    }, [deleted, navigate, backUrl]);

    useEffect(() => {
        if (!newWebhook && !changelog && activeTab === 'history' && idWebhook) {
            dispatch(getWebhookDetailHistoryFetch(idWebhook));
        }
    }, [activeTab, changelog, dispatch, idWebhook, newWebhook]);

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData } as WebhookRecordType;
        const newErrors = { ...editErrors };
        (newData as unknown as Record<string, unknown>)[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleDeleteWebhook = () => {
        setConfirmParams({
            title: translate('webhookDetail.deleteWebhook'),
            content: translate('webhookDetail.deleteWebhookConfirm'),
            action: () => dispatch(deleteWebhookFetch(idWebhook)),
        });
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            setConfirmParams({
                title: translate(
                    newWebhook ? 'webhookDetail.titleNew' : 'webhookDetail.title',
                    { idWebhook }
                ),
                content: translate(newWebhook ? 'webhookDetail.createConfirm' : 'webhookDetail.saveConfirm'),
                action: () => {
                    const formatData = { ...editData };
                    if (newWebhook) {
                        dispatch(createWebhookDetailFetch(formatData));
                    } else {
                        dispatch(updateWebhookDetailFetch({ idWebhook, data: formatData }));
                    }
                },
            });
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <TopBlock
                data={data}
                idWebhook={idWebhook}
                newWebhook={newWebhook}
                disableEdit={disableEdit}
                disableDelete={disableDelete}
                handleSave={handleSave}
                handleDeleteWebhook={handleDeleteWebhook}
                activeTab={activeTab}
                onChangeTab={setActiveTab}
            />
            {activeTab === 'detail' && (
                <form ref={formRef}>
                    {loading && <Loading />}
                    <FormBlock>
                        <Detail
                            onChange={onChange}
                            editData={editData}
                            editErrors={editErrors}
                            newWebhook={newWebhook}
                            disableEdit={disableEdit}
                            boolNumberCodes={boolNumberCodes || []}
                        />
                    </FormBlock>
                </form>
            )}
            {activeTab === 'history' && !newWebhook && (
                <>
                    {loading && <Loading />}
                    <HistoryBlock
                        changelog={changelog}
                        webhookTypeCodesObject={webhookTypeCodesObject}
                        boolNumberCodesObject={boolNumberCodesObject}
                        methodCodesObject={methodCodesObject}
                    />
                </>
            )}
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default WebhookDetailContainer;
